import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getCompanyInfo, removeCompanyData } from 'store/company';
import { LOADING, SUCCEEDED, FAILED } from 'constants/fetch';
import { useHttp } from 'hooks/http.hook';
import { API_VERSION_V2 } from 'endpointsConst';
import Loading from 'components/Loading';
import ScrollToTopOnMount from '@/ScrollToTop';
import PageContainer from 'components/common/PageContainer';
import EmployeesSection from 'components/common/InfoPopup/EmployeesSection';
import CompanyReviews from 'components/common/InfoPopup/CompanyReviews';
import CompanyDocuments from 'components/common/InfoPopup/CompanyDocuments';

import CompanyHeader from './CompanyHeader';
import CompanyTabs from './CompanyTabs';
import AboutCompany from './AboutCompany';

import styles from './styles.module.scss';

export default function CompanyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { request } = useHttp();
  const [activeTab, setActiveTab] = useState('about');
  const [documentsData, setDocumentsData] = useState<any[] | null>(null);

  const companyData = useSelector((state: any) => state.company.companyData);
  const companyLogo = useSelector((state: any) => state.company.logo);
  const status = useSelector((state: any) => state.company.status);

  useEffect(() => {
    if (id) {
      dispatch(getCompanyInfo({ id, lang: i18n.language }) as any);
    }
    return () => {
      dispatch(removeCompanyData());
    };
  }, [id, i18n.language, dispatch]);

  useEffect(() => {
    if (activeTab === 'documents' && id && documentsData === null) {
      request(`${API_VERSION_V2}/companies/${id}/documents`)
        .then((res: any) => {
          setDocumentsData(res.data?.items || res.data || []);
        })
        .catch(() => {
          setDocumentsData([]);
        });
    }
  }, [activeTab, id, documentsData, request]);

  if (status === LOADING) {
    return (
      <>
        <ScrollToTopOnMount />
        <PageContainer>
          <Loading />
        </PageContainer>
      </>
    );
  }

  if (status === FAILED) {
    return (
      <>
        <ScrollToTopOnMount />
        <PageContainer>
          <div className={styles.error}>{t('common.error')}</div>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <ScrollToTopOnMount />
      <PageContainer>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <span className={styles.backArrow}>&#8249;</span>
          {t('common.goBack')}
        </button>

        <div className={styles.card}>
          <CompanyHeader
            companyData={companyData}
            companyLogo={companyLogo}
          />
          <CompanyTabs activeTab={activeTab} onTabChange={setActiveTab} />

          {activeTab === 'about' && (
            <AboutCompany companyData={companyData} companyId={id} />
          )}
          {activeTab === 'employees' && (
            <div className={styles.tabContent}>
              <EmployeesSection employees={null} companyId={id} />
            </div>
          )}
          {activeTab === 'reviews' && (
            <div className={styles.tabContent}>
              <CompanyReviews companyId={id} />
            </div>
          )}
          {activeTab === 'documents' && (
            <div className={styles.tabContent}>
              {documentsData === null ? (
                <Loading />
              ) : (
                <CompanyDocuments
                  documentsData={documentsData}
                  companyRole={companyData.company_roles}
                  onRequestDocument={undefined}
                />
              )}
            </div>
          )}
        </div>
      </PageContainer>
    </>
  );
}
